import React from 'react';
import {useTranslation} from 'react-i18next';

import type {
    AriaMenuItem,
    AriaSubMenuContentHelpers,
    ColorScheme,
} from '@pexip/components';
import {AriaMenu, Icon, IconTypes, Tooltip} from '@pexip/components';
import {TestId as MediaTestId} from '@pexip/media-components';

import {TestId} from '../../../test/testIds';
import type {UserMenuStep} from '../../types';

type SubMenuContent =
    React.ReactNode | ((helpers: AriaSubMenuContentHelpers) => React.ReactNode);

export type UserMenuProps = {
    hasMedia?: boolean;
    isDirectMedia?: boolean;
    isFeedbackEnabled?: boolean;
    isInMeeting: boolean;
    canChangeLayout?: boolean;
    canChangePersonalLayout?: boolean;
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    pluginActions: AriaMenuItem[];
    downloadLogWithName: () => void;
    openFeedbackForm: () => void;
    onLeave?: () => void;
    emitStep: (step: UserMenuStep) => void;
    openStats: () => void;
    subMenuContent: {
        videoAndSound: SubMenuContent;
        joining: SubMenuContent;
        quality: SubMenuContent;
        language: SubMenuContent;
        layouts: SubMenuContent;
        accessibility: SubMenuContent;
        advanced: SubMenuContent;
        about: SubMenuContent;
    };
    button: React.ReactElement;
    onOpenChange: (isOpen: boolean) => void;
    onGoBack: () => void;
    colorScheme?: ColorScheme;
};

export const UserMenu: React.FC<UserMenuProps> = ({
    hasMedia,
    isDirectMedia,
    isFeedbackEnabled,
    isInMeeting,
    canChangeLayout,
    canChangePersonalLayout,
    showLeave = false,
    showQuality = true,
    showMediaStatistics = true,
    pluginActions,
    downloadLogWithName,
    openFeedbackForm,
    onLeave,
    emitStep,
    openStats,
    subMenuContent,
    button,
    onOpenChange,
    onGoBack,
    colorScheme,
}) => {
    const {t} = useTranslation();

    const settingsBackProps = {
        titleTestId: MediaTestId.PanelHeaderTitle,
        backButtonTestId: MediaTestId.ButtonSettingsBack,
    };

    const layoutLabel = canChangeLayout
        ? t('settings.meeting-layout', 'Meeting layout')
        : t('settings.layout', 'Layout');

    const coreItems: AriaMenuItem[] = [
        ...(hasMedia
            ? [
                  {
                      key: 'video-and-sound',
                      label: t('settings.video-and-sound', 'Video and sound'),
                      testId: TestId.ButtonMeetingDeviceSettings,
                      onItemClick: () => emitStep('video-and-sound'),
                      subMenu: {
                          title: t(
                              'settings.video-and-sound',
                              'Video and sound',
                          ),
                          content: subMenuContent.videoAndSound,
                          ...settingsBackProps,
                      },
                  },
              ]
            : []),
        {
            key: 'joining',
            label: t('settings.joining', 'Joining'),
            testId: TestId.TabSettingsJoiningSettings,
            onItemClick: () => emitStep('joining'),
            subMenu: {
                title: t('settings.joining', 'Joining'),
                content: subMenuContent.joining,
                ...settingsBackProps,
            },
        },
        ...(showQuality
            ? [
                  {
                      key: 'quality',
                      label: t('settings.quality', 'Quality'),
                      testId: TestId.LinkQuality,
                      onItemClick: () => emitStep('quality'),
                      subMenu: {
                          title: t('settings.quality', 'Quality'),
                          content: subMenuContent.quality,
                          ...settingsBackProps,
                      },
                  },
              ]
            : []),
        {
            key: 'language',
            label: t('settings.languages', 'Language'),
            testId: TestId.LinkLanguages,
            onItemClick: () => emitStep('language'),
            subMenu: {
                title: t('settings.languages', 'Language'),
                content: subMenuContent.language,
                ...settingsBackProps,
            },
        },
        ...(isInMeeting &&
        (Boolean(canChangeLayout) || Boolean(canChangePersonalLayout)) &&
        !isDirectMedia
            ? [
                  {
                      key: 'layouts',
                      label: layoutLabel,
                      testId: TestId.TabSettingsMeetingLayout,
                      onItemClick: () => emitStep('layouts'),
                      subMenu: {
                          title: layoutLabel,
                          content: subMenuContent.layouts,
                          ...settingsBackProps,
                      },
                  },
              ]
            : []),
        {
            key: 'accessibility',
            label: t('settings.accessibility', 'Accessibility'),
            testId: TestId.ButtonAccessibilitySettings,
            onItemClick: () => emitStep('accessibility'),
            subMenu: {
                title: t('settings.accessibility', 'Accessibility'),
                content: subMenuContent.accessibility,
                ...settingsBackProps,
            },
        },
        {
            key: 'advanced',
            label: t('settings.advanced', 'Advanced'),
            testId: TestId.TabSettingsAdvancedSettings,
            onItemClick: () => emitStep('advanced'),
            subMenu: {
                title: t('settings.advanced', 'Advanced'),
                content: subMenuContent.advanced,
                ...settingsBackProps,
            },
        },
    ];

    const nerdItems: AriaMenuItem[] = [
        {
            key: 'about',
            label: t('settings.about', 'About'),
            testId: TestId.LinkAboutPexip,
            onItemClick: () => emitStep('about'),
            subMenu: {
                title: t('settings.about', 'About'),
                content: subMenuContent.about,
                ...settingsBackProps,
            },
        },
        ...(isFeedbackEnabled
            ? [
                  {
                      key: 'feedback',
                      label: t('meeting.menu-feedback', 'Send feedback'),
                      testId: TestId.SubmitFeedback,
                      icon: (
                          <Icon
                              source={IconTypes.IconOpenInNew}
                              size="compact"
                          />
                      ),
                      iconPosition: 'end' as const,
                      onItemClick: openFeedbackForm,
                  },
              ]
            : []),
        ...(isInMeeting && showMediaStatistics
            ? [
                  {
                      key: 'stats',
                      label: t('stats.media-stats', 'Media statistics'),
                      testId: TestId.ButtonSelfviewQuality,
                      onItemClick: openStats,
                  },
              ]
            : []),
        {
            key: 'download-log',
            label: t('common.download-log', 'Download log'),
            testId: TestId.LinkDownloadLog,
            onItemClick: downloadLogWithName,
            closeMenuOnItemClick: false,
        },
    ];

    const hasPlugins = pluginActions.length > 0;

    const pluginItems: AriaMenuItem[] = pluginActions.map((item, index) => ({
        ...item,
        withTopDivider: index === 0,
    }));

    const firstNerdItem = nerdItems[0];
    if (firstNerdItem && hasPlugins) {
        firstNerdItem.withTopDivider = true;
    }

    const leaveItems: AriaMenuItem[] = showLeave
        ? [
              {
                  key: 'leave',
                  label: t('meeting.leave', 'Leave'),
                  testId: TestId.ButtonLeaveUserMenu,
                  icon: (
                      <Icon
                          source={IconTypes.IconPhoneHorisontal}
                          size="compact"
                      />
                  ),
                  isDanger: true,
                  withTopDivider: true,
                  onItemClick: onLeave,
              },
          ]
        : [];

    const items: AriaMenuItem[] = [
        ...coreItems,
        ...pluginItems,
        ...nerdItems,
        ...leaveItems,
    ];

    return (
        <AriaMenu
            items={items}
            button={button}
            renderButton={triggerButton => (
                <Tooltip
                    text={t('settings.menu-button-tooltip', 'Settings')}
                    position="bottomEnd"
                >
                    {triggerButton}
                </Tooltip>
            )}
            testId={TestId.UserMenu}
            popoverPlacement="bottom end"
            colorScheme={colorScheme}
            onOpenChange={onOpenChange}
            onGoBack={onGoBack}
            backButtonLabel={t('common.back', 'Back')}
            showSubMenuIndicator={false}
        />
    );
};
